/**
 * A lawn plan, sold plainly (owner, Sep 19 2026): "when we're going to treat the lawn — four fertilizers, these
 * months … make it sound like so much that they get for the price."
 *
 * Draws one plan picture from the server (services/bidPage.planPicture): how many of each application and the
 * months they go down, the year as a strip, what is in the plan, and what that works out to per application.
 * Shared by the expandable line on the bid (BidLines) and the add/upgrade block above Accept (BidExtras).
 *
 * On the bid they sign, the year strip can slide a month earlier or later (owner, Sep 20 2026: "adjust timing
 * on the treatment plans for the client side"). Same applications, same price. The office BidLines view is
 * display-only — pass `onShift` only on the customer's page.
 *
 * `mowed` (default true) is whether we mow the yard. A yard we do not mow is never told its applications ride a
 * mowing visit (owner, Oct 14 2026 — Scott, 415 Derby Lane): each one is its own visit.
 */
import React from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';

export type PlanMonth = {
  month: string; index: number; words: string;
  services?: string[]; earlier?: boolean; later?: boolean;
};
export type PlanPicture = {
  plan: string; name: string; kicker?: string; tag?: string;
  monthly: number; yearly: number; base_monthly?: number;
  applications: number; months_with_a_visit: number; per_application: number | null;
  services: { service: string; count: number; word: string; help: string; extra: number; months: string[] }[];
  months: PlanMonth[];
  includes: string[]; not_included?: string[]; items: Record<string, number>; changed?: boolean;
  value?: { per_application: number | null; local_label?: string; local_range?: string };
  on_bid?: boolean;
  /** The owner's price for this plan if we also mow the yard — on a plan they are adding to, his price carried onto it. */
  mowed_monthly?: number | null;
};
export type PlanShift = { from: number; dir: 1 | -1 };

const money = (n: number) => `$${(Math.round(Number(n) * 100) / 100).toFixed(2).replace(/\.00$/, '')}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const NUM = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve'];
const countWord = (n: number) => NUM[n] || String(n);
// "Four fertilizer treatments", "One soil test", "Two core aerations".
const OWN_NOUN: Record<string, [string, string]> = { soiltest: ['soil test', 'soil tests'], core: ['core aeration', 'core aerations'], rye: ['winter rye overseeding', 'winter rye overseedings'] };
const named = (service: string, word: string, n: number) => (OWN_NOUN[service] ? OWN_NOUN[service][n === 1 ? 0 : 1] : `${word} ${n === 1 ? 'treatment' : 'treatments'}`);
const listMonths = (m: string[]) => (m.length <= 1 ? m.join('') : `${m.slice(0, -1).join(', ')} and ${m.slice(-1)[0]}`);

/**
 * A month slides only into an EMPTY one.
 *
 * It used to merge whenever the two shared no service — and a merged month can
 * never be pulled apart again, because from then on the cell holds both and
 * this same check blocks every way back. The customer was stuck with whatever
 * they had just done (owner, Sep 21 2026: "if I move one to a month then they
 * all combine, no way to uncombine" … "they still combine"). The server's rule
 * matches exactly (services/planTiming.shiftMonth), so what the page offers is
 * what it will accept.
 */
function canSlide(months: PlanMonth[], from: number, dir: number) {
  const to = from + dir;
  if (to < 0 || to > 11) return false;
  const src = months.find(m => m.index === from);
  if (!src) return false;
  return !months.find(m => m.index === to);
}

/** Replay adjacent slides on the year the server drew. Display only — the server applies the same list on accept. */
export function monthsAfter(months: PlanMonth[], shifts: PlanShift[]): PlanMonth[] {
  // Typed as PlanMonth[] on purpose: `merged` below is a PlanMonth, whose
  // `services` is optional, and without this the inferred element type has it
  // required — which fails the build, and Railway builds the client, so it
  // takes the API deploy down with it.
  let cur: PlanMonth[] = months.map(m => ({ ...m, services: [...(m.services || [])] }));
  for (const s of shifts) {
    const to = s.from + s.dir;
    if (!canSlide(cur, s.from, s.dir)) continue;
    const src = cur.find(m => m.index === s.from)!;
    const dst = cur.find(m => m.index === to);
    const rest = cur.filter(m => m.index !== s.from && m.index !== to);
    const merged: PlanMonth = {
      index: to,
      month: dst?.month || MONTH_NAMES[to],
      words: dst ? `${dst.words}, ${src.words}` : src.words,
      services: [...(dst?.services || []), ...src.services],
    };
    cur = [...rest, merged].sort((a, b) => a.index - b.index);
  }
  return cur.map(m => ({ ...m, earlier: canSlide(cur, m.index, -1), later: canSlide(cur, m.index, 1) }));
}

export default function BidPlanYear({ plan, how, shifts, onShift, onUndo, onReset, mowed = true }: {
  plan: PlanPicture; how?: string | null;
  mowed?: boolean;
  shifts?: PlanShift[];
  onShift?: (from: number, dir: 1 | -1) => void;
  onUndo?: () => void;
  onReset?: () => void;
}) {
  const shown = monthsAfter(plan.months, shifts || []);
  const byIndex = new Map(shown.map(m => [m.index, m]));
  const live = typeof onShift === 'function';
  return (
    <div className="space-y-4">
      {/* What goes down, and when */}
      <div>
        <p className="text-[11px] uppercase tracking-widest text-gray-600 mb-2">What your lawn gets, and when</p>
        <ul className="space-y-2.5">
          {plan.services.map(s => {
            const months = shown.filter(m => (m.services || []).includes(s.service)).map(m => m.month);
            return (
              <li key={s.service} className="flex gap-3">
                <span className="flex-shrink-0 h-9 w-9 rounded-xl bg-brand-50 border border-brand-200 text-brand-700 text-sm font-bold flex items-center justify-center tabular-nums">{s.count}×</span>
                <span className="min-w-0">
                  <span className="block text-[15px] font-semibold text-gray-900">
                    {countWord(s.count)} {named(s.service, s.word, s.count)}
                    {s.extra > 0 && <span className="ml-1.5 align-middle text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">{s.extra} added by you</span>}
                  </span>
                  <span className="block text-sm text-gray-600">{listMonths(months.length ? months : s.months)}.</span>
                  {s.help && <span className="block text-xs text-gray-500 mt-0.5">{s.help}</span>}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* The year as a strip */}
      <div>
        <p className="text-[11px] uppercase tracking-widest text-gray-600 mb-2">Your year at a glance</p>
        {live && (
          <div className="mb-2 flex items-start justify-between gap-2 flex-wrap">
            <p className="text-xs text-gray-500 flex-1 min-w-0">
              A month that does not work? Slide it earlier or later. Same treatments, same price.{mowed ? ' We still come on a mow that month.' : ''} Two
              that land on one month are done on the same visit.
            </p>
            {(onUndo || onReset) && (
              <span className="flex items-center gap-2 flex-shrink-0">
                {onUndo && <button type="button" onClick={onUndo} className="text-xs font-semibold text-brand-700 underline underline-offset-2">Undo</button>}
                {onReset && <button type="button" onClick={onReset} className="text-xs text-gray-500 underline underline-offset-2">Start over</button>}
              </span>
            )}
          </div>
        )}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
          {MONTHS.map((m, i) => {
            const cell = byIndex.get(i);
            const words = cell?.words;
            return (
              <div key={m} className={`rounded-lg px-2 py-1.5 border ${words ? 'bg-brand-50 border-brand-200' : 'bg-gray-50 border-gray-100'}`}>
                <p className={`text-[10px] uppercase tracking-wide font-semibold ${words ? 'text-brand-700' : 'text-gray-600'}`}>{m}</p>
                <p className={`text-[11px] leading-tight mt-0.5 break-words ${words ? 'text-gray-800' : 'text-gray-500'}`}>{words ? cap(words.toLowerCase()) : 'rest'}</p>
                {live && words && (
                  <div className="flex items-center justify-between mt-1 -mx-0.5">
                    <button type="button" aria-label={`Move ${m} earlier`} disabled={!cell?.earlier}
                      onClick={e => { e.stopPropagation(); if (cell?.earlier) onShift?.(i, -1); }}
                      className="h-7 w-7 rounded-md flex items-center justify-center text-brand-700 disabled:opacity-20 disabled:pointer-events-none hover:bg-brand-100">
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button type="button" aria-label={`Move ${m} later`} disabled={!cell?.later}
                      onClick={e => { e.stopPropagation(); if (cell?.later) onShift?.(i, 1); }}
                      className="h-7 w-7 rounded-md flex items-center justify-center text-brand-700 disabled:opacity-20 disabled:pointer-events-none hover:bg-brand-100">
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* What is in it */}
      {plan.includes.length > 0 && (
        <ul className="space-y-1">
          {plan.includes.map(w => (
            <li key={w} className="flex items-start gap-2 text-sm text-gray-700"><Check className="h-4 w-4 mt-0.5 text-emerald-600 flex-shrink-0" />{w}</li>
          ))}
        </ul>
      )}

      {/* What that works out to */}
      <div className="rounded-2xl bg-brand-600 text-black px-4 py-3 shadow-brand">
        <p className="text-sm leading-relaxed">
          <b>{plan.applications} applications</b> over <b>{shown.length} months</b> for <b>{money(plan.monthly)} a month</b>
          {plan.value?.per_application ? <>, about <b>{money(plan.value.per_application)}</b> an application</> : null}.
          {plan.value?.local_range ? <> Around here a single application typically runs {plan.value.local_range}.</> : null}
        </p>
        <p className="text-xs text-black/75 mt-1">{mowed
          ? 'Every application is done on a mowing visit we are already making, so there is no extra trip to pay for. The same price every month. No contract. Pause any time.'
          : 'We come out for these ourselves at the right time of year, not on a mow. The same price every month. No contract. Pause any time.'}</p>
      </div>

      {how && <p className="text-xs text-gray-500 leading-relaxed">{how}</p>}
    </div>
  );
}
